import { useState, useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useSettings } from '../../context/SettingsContext';

export function WhatsAppButton({ 
  message = 'Hello! I would like to know more about your tour packages.',
  position = 'bottom-right'
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);
  const { settings } = useSettings();

  // Get phone number from settings, removing any spaces or special chars except +
  const phoneNumber = (settings.whatsapp_number || '+919181268900').replace(/[\s-]/g, '');

  // Format WhatsApp URL
  const whatsappUrl = `https://wa.me/${phoneNumber.replace('+', '')}?text=${encodeURIComponent(message)}`;

  // Position classes
  const positionClasses = {
    'bottom-right': 'right-6 bottom-24',
    'bottom-left': 'left-6 bottom-24',
  };

  // Hide tooltip after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowTooltip(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div 
      className={cn(
        "fixed z-50",
        positionClasses[position]
      )}
      data-testid="whatsapp-button-container"
    >
      {/* Tooltip */}
      {showTooltip && (
        <div 
          className={cn(
            "absolute bottom-full mb-3 right-0 bg-white rounded-lg shadow-lg p-3 min-w-[200px] animate-bounce-slow",
            "before:content-[''] before:absolute before:bottom-[-8px] before:right-6 before:border-8 before:border-transparent before:border-t-white"
          )}
        >
          <button 
            onClick={() => setShowTooltip(false)}
            className="absolute top-1 right-1 p-1 text-gray-400 hover:text-gray-600"
            aria-label="Close tooltip"
          >
            <X className="w-3 h-3" />
          </button>
          <p className="text-sm text-gray-700 font-medium">
            Need help? Chat with us!
          </p>
        </div>
      )}

      {/* WhatsApp Button */}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "group flex items-center gap-3 bg-[#25D366] text-white rounded-full shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105",
          isHovered ? "pl-5 pr-6 py-3" : "p-4"
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid="whatsapp-button"
        aria-label="Chat on WhatsApp"
      >
        {/* Ripple Effect */}
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-25"></span>
        
        {/* Icon */}
        <MessageCircle className="w-6 h-6 relative z-10 fill-current" />
        
        {/* Text - shows on hover */}
        <span 
          className={cn(
            "font-medium whitespace-nowrap transition-all duration-300 overflow-hidden",
            isHovered ? "w-auto opacity-100" : "w-0 opacity-0"
          )}
        >
          Chat with us
        </span>
      </a>

      {/* Pulse ring animation */}
      <div className="absolute inset-0 rounded-full bg-[#25D366] animate-pulse opacity-30 -z-10"></div>
    </div>
  );
}
